import { useState } from 'react';
import { CaretDown, CaretRight, Camera, Warning, User, Package, MapPin } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { StorageThumb } from '@/components/StorageMedia';
import { formatJST } from '@/utils/datetime';

/**
 * Inline expandable 検収履歴 timeline for a single PO line.
 * Renders each receipt event (qty, date, receiver, photo thumbnail, discrepancy note).
 * Clicking a thumbnail opens a zoomed modal.
 *
 * Props:
 *   receipts    — array of {qty, date, by, location_id, photo_url, discrepancy_note, recorded_at}
 *   locations   — array of {id, name} for resolving location_id → name
 *   testIdBase  — string prefix for data-testid on child elements
 */
export default function ReceiptLineTimeline({ receipts, locations = [], testIdBase = 'receipt-timeline' }) {
  const [open, setOpen] = useState(false);
  const [zoomedPhoto, setZoomedPhoto] = useState(null);

  if (!receipts || receipts.length === 0) return null;
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  const locName = (id) => (locations.find((l) => l.id === id) || {}).name;

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        data-testid={`${testIdBase}-toggle`}
        className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded transition-colors ${open ? 'bg-emerald-100 text-emerald-800' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
      >
        {open ? <CaretDown size={10} weight="bold" /> : <CaretRight size={10} weight="bold" />}
        検収履歴 {receipts.length}件
      </button>
      {open && (
        <div className="mt-1.5 pl-3 border-l-2 border-emerald-200 space-y-1.5"
          data-testid={`${testIdBase}-list`}>
          {receipts.map((r, i) => {
            const dt = formatJST(r.recorded_at || r.date);
            const hasPhoto = !!r.photo_url;
            const hasNote = !!r.discrepancy_note;
            const loc = locName(r.location_id);
            return (
              <div key={`${r.recorded_at || r.date || 'receipt'}-${i}`}
                data-testid={`${testIdBase}-item-${i}`}
                className={`text-[11px] bg-white rounded border ${hasNote ? 'border-amber-200 bg-amber-50/40' : 'border-zinc-200'} p-1.5`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="tabular-nums text-zinc-500">{dt}</span>
                    <span className="inline-flex items-center gap-0.5 font-mono font-semibold text-emerald-700">
                      <Package size={9} weight="fill" />+{r.qty}
                    </span>
                    {r.by && (
                      <span className="inline-flex items-center gap-0.5 text-zinc-600">
                        <User size={9} weight="duotone" />{r.by}
                      </span>
                    )}
                    {loc && (
                      <span className="inline-flex items-center gap-0.5 text-zinc-500">
                        <MapPin size={9} weight="duotone" />{loc}
                      </span>
                    )}
                  </div>
                  {hasPhoto && (
                    <button
                      type="button"
                      onClick={() => setZoomedPhoto(r.photo_url)}
                      data-testid={`${testIdBase}-thumb-${i}`}
                      className="w-10 h-10 rounded overflow-hidden bg-zinc-100 flex-shrink-0 hover:ring-2 hover:ring-emerald-400 transition-all"
                    >
                      <StorageThumb
                        path={r.photo_url}
                        alt="proof"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </button>
                  )}
                </div>
                {hasNote && (
                  <div className="mt-1 flex items-start gap-1 text-amber-800">
                    <Warning size={11} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                    <span className="flex-1">{r.discrepancy_note}</span>
                  </div>
                )}
                {!hasPhoto && !hasNote && r.location_id === null && (
                  <div className="text-[10px] text-zinc-400 mt-0.5">写真・備考なし</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Zoom modal */}
      <Dialog open={!!zoomedPhoto} onOpenChange={(o) => { if (!o) setZoomedPhoto(null); }}>
        <DialogContent className="max-w-3xl p-2" data-testid={`${testIdBase}-zoom`}>
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-1.5">
              <Camera size={16} weight="duotone" className="text-emerald-600" />検収写真
            </DialogTitle>
          </DialogHeader>
          {zoomedPhoto && (
            <StorageThumb
              path={zoomedPhoto}
              alt="proof"
              className="w-full max-h-[70vh] object-contain rounded bg-zinc-100"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
